export const ORIENTATION = <const>{
	LANDSCAPE: 'landscape',
	PORTRAIT: 'portrait',
};

export const SIZE_TYPE = <const>{
	PREVIEW: 'preview',
	THUMB: 'thumb',
	ZOOM: 'zoom',
};

export type Size = typeof SIZE_TYPE[keyof typeof SIZE_TYPE];
export type Dims = {width: number; height: number};
export type Orientation = typeof ORIENTATION[keyof typeof ORIENTATION];

export const PROXY_MAX_LENGTH: Record<Size, number> = <const>{
	preview: 500,
	thumb: 150,
	zoom: 1000,
};

export const isPortrait = ({height, width}: Dims) => height > width;
export const isLandscape = ({height, width}: Dims) => width > height;
export const isSquare = ({height, width}: Dims) => width === height;

/**
 * Fit image to container dimensions
 * @return Fitted image dimensions
 */
export const getFittedDims = (imageDims: Dims, containerDims: Dims) => {
	if (isPortrait(imageDims)) {
		const ratio = imageDims.width / imageDims.height;
		let newHeight = Math.min(imageDims.height, containerDims.height);
		let newWidth = newHeight * ratio;
		if (newWidth > containerDims.width) {
			newWidth = containerDims.width;
			newHeight = newWidth / ratio;
		}
		return {width: newWidth, height: newHeight};
	}
	const ratio = imageDims.height / imageDims.width;
	let newWidth = Math.min(imageDims.width, containerDims.width);
	let newHeight = newWidth * ratio;
	if (newHeight > containerDims.height) {
		newHeight = containerDims.height;
		newWidth = newHeight / ratio;
	}
	return {width: newWidth, height: newHeight};
};

/**
 * Get dimensions of image based on size and (optionally) container dims (fit to container)
 * @returns New image dimensions
 */
export const getDims = (size: Size, imageDims: Dims, containerDims?: Dims) => {
	if (isPortrait(imageDims)) {
		const maxSize = Math.min(PROXY_MAX_LENGTH[size], imageDims.height);
		const newDims = {
			height: maxSize,
			width: Math.round(maxSize * (imageDims.width / imageDims.height)),
		};
		return containerDims ? getFittedDims(newDims, containerDims) : newDims;
	}
	const maxSize = Math.min(PROXY_MAX_LENGTH[size], imageDims.width);
	const newDims = {
		height: Math.round(maxSize * (imageDims.height / imageDims.width)),
		width: maxSize,
	};
	return containerDims ? getFittedDims(newDims, containerDims) : newDims;
};

/**
 * Given a container dimensions, deduce the appropriate size
 * @reurns Image size
 */
export const getClosestSize = (
	{width, height}: Dims,
	orientation: Orientation,
): Size => {
	if (orientation === ORIENTATION.PORTRAIT) {
		if (height <= PROXY_MAX_LENGTH.thumb) return SIZE_TYPE.THUMB;
		if (height > PROXY_MAX_LENGTH.preview) return SIZE_TYPE.ZOOM;
		return SIZE_TYPE.PREVIEW;
	}
	if (width <= PROXY_MAX_LENGTH.thumb) return SIZE_TYPE.THUMB;
	if (width > PROXY_MAX_LENGTH.preview) return SIZE_TYPE.ZOOM;
	return SIZE_TYPE.PREVIEW;
};

/**
 * Get preview image dimensions
 * @return Preview image dimensions
 */
export const getPreviewDims = ({width, height}: Dims, containerDims?: Dims) =>
	getDims('preview', {width, height}, containerDims);

/**
 * Get thumbnail image dimensions
 * @return Thumbnail image dimensions
 */
export const getThumbDims = ({width, height}: Dims, containerDims?: Dims) =>
	getDims('thumb', {width, height}, containerDims);

/**
 * Get zoom image dimensions
 * @return Zoom image dimensions
 */
export const getZoomDims = ({width, height}: Dims, containerDims?: Dims) =>
	getDims('zoom', {width, height}, containerDims);

/**
 * Get image dimensions based on size, and container dimensions (fit to container)
 * @return Sized image dimensions
 */
export const getSizedDims = (
	size: Size,
	imageDims: Dims,
	containerDims: Dims,
) => {
	switch (size) {
		case 'thumb':
			return getThumbDims(imageDims, containerDims);
		case 'preview':
			return getPreviewDims(imageDims, containerDims);
		case 'zoom':
			return getZoomDims(imageDims, containerDims);
	}
};
